import {
  resolveMilestoneLinkedDocument,
  resolvePlaceholderRoleNameEntries,
  resolvePlaceholderSignatureState,
} from "./isfPlaceholderService";

const STATUS_RANK = { overdue: 0, pending: 1, fulfilled: 2, waived: 3, na: 4 };

/** Rich gentle status colors — aligned with IsfCommandCenter.scss tokens */
export const CC_COLORS = {
  fulfilled: "#4A9F7A",
  pending: "#4F8FD4",
  overdue: "#D16565",
  na: "#E8EDEB",
  signed: "#4A9F7A",
  await: "#D9A441",
};

export function readinessHex(pct) {
  const p = Number(pct) || 0;
  if (p >= 90) return CC_COLORS.fulfilled;
  if (p >= 70) return CC_COLORS.await;
  return CC_COLORS.overdue;
}

export function readinessCssVar(pct) {
  const p = Number(pct) || 0;
  if (p >= 90) return "var(--cc-green)";
  if (p >= 70) return "var(--cc-amber)";
  return "var(--cc-red)";
}

export function normalizeStatus(raw) {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (s === "fulfilled") return "fulfilled";
  if (s === "overdue") return "overdue";
  if (s === "waived") return "waived";
  if (s === "pending") return "pending";
  return "pending";
}

export function dueBucket(dueDate, status) {
  const st = normalizeStatus(status);
  if (st === "overdue") return "overdue";
  if (!dueDate) return "gt30";
  const dueMs = new Date(dueDate).setHours(0, 0, 0, 0);
  if (Number.isNaN(dueMs)) return "gt30";
  const todayMs = new Date().setHours(0, 0, 0, 0);
  const diff = Math.round((dueMs - todayMs) / 86400000);
  if (diff < 0) return "overdue";
  if (diff <= 7) return "le7";
  if (diff <= 14) return "d8_14";
  if (diff <= 30) return "d15_30";
  return "gt30";
}

export function daysFromDue(dueDate, status) {
  if (normalizeStatus(status) !== "overdue" || !dueDate) return 0;
  const todayMs = new Date().setHours(0, 0, 0, 0);
  const dueMs = new Date(dueDate).setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((todayMs - dueMs) / 86400000));
}

function pickWorseStatus(a, b) {
  return (STATUS_RANK[normalizeStatus(a)] ?? 99) <= (STATUS_RANK[normalizeStatus(b)] ?? 99)
    ? a
    : b;
}

function resolveSigState(ph, doc) {
  return resolvePlaceholderSignatureState(ph, doc);
}

function placeholderKey(row) {
  return String(
    row?.StudyPlaceholderId ?? row?.studyPlaceholderId ?? "",
  ).toLowerCase();
}

function siteKey(row) {
  const id = row?.SiteIdRef ?? row?.siteIdRef ?? "";
  return id ? String(id).toLowerCase() : "__study__";
}

function milestoneCode(row) {
  const code = String(row?.MilestoneCode ?? row?.milestoneCode ?? "").trim();
  return code || "(Unassigned)";
}

export function scopeCells(cells, siteFilter) {
  if (!siteFilter || siteFilter === "all") return cells.filter((c) => c.status !== "na");
  return cells.filter(
    (c) => c.status !== "na" && c.siteId === siteFilter,
  );
}

export function aggregateCells(cells) {
  const scoped = cells.filter((c) => c.status !== "na" && c.isRequired);
  const a = {
    req: scoped.length,
    ful: 0,
    pen: 0,
    ovd: 0,
    le7: 0,
    d8_14: 0,
    d15_30: 0,
    gt30: 0,
  };
  scoped.forEach((c) => {
    if (c.status === "fulfilled") a.ful += 1;
    else if (c.status === "overdue") a.ovd += 1;
    else {
      a.pen += 1;
      if (c.bucket === "le7") a.le7 += 1;
      else if (c.bucket === "d8_14") a.d8_14 += 1;
      else if (c.bucket === "d15_30") a.d15_30 += 1;
      else if (c.bucket === "gt30") a.gt30 += 1;
    }
  });
  a.pct = a.req ? Math.round((1000 * a.ful) / a.req) / 10 : 0;
  return a;
}

export function signatureAggregate(cells) {
  const scoped = cells.filter((c) => c.reqSig);
  const a = { req: 0, signed: 0, await: 0 };
  scoped.forEach((c) => {
    const req = Number(c.sigReq ?? (c.reqSig ? 1 : 0));
    const signed = Number(c.sigSigned ?? (c.sig === "signed" ? 1 : 0));
    a.req += req;
    a.signed += signed;
    a.await += Math.max(0, req - signed);
  });
  a.pct = a.req ? Math.round((1000 * a.signed) / a.req) / 10 : 0;
  return a;
}

export function filedAwaitingSign(cells) {
  return cells.reduce((sum, c) => {
    if (!c.reqSig || c.status !== "fulfilled") return sum;
    const pending = Number(c.sigReq ?? 1) - Number(c.sigSigned ?? (c.sig === "signed" ? 1 : 0));
    return sum + Math.max(0, pending);
  }, 0);
}

export function buildCommandCenterModel({
  summary = null,
  placeholderDetails = [],
  milestoneSummary = [],
  atRiskDetail = [],
  siteComparison = [],
  placeholders = [],
  documents = [],
  roleOptions = [],
}) {
  const phById = new Map();
  (placeholders || []).forEach((ph) => {
    const key = placeholderKey(ph);
    if (key) phById.set(key, ph);
  });

  const cellMap = new Map();
  const siteMap = new Map();
  const msMap = new Map();

  (placeholderDetails || []).forEach((row) => {
    const isRequired =
      row?.IsRequired === 1 ||
      row?.IsRequired === true ||
      row?.isRequired === 1;
    const status = normalizeStatus(row?.Status ?? row?.status);
    if (status === "waived") return;

    const sid = siteKey(row);
    const code = milestoneCode(row);
    const ph = { ...row, ...(phById.get(placeholderKey(row)) || {}) };
    const doc = resolveMilestoneLinkedDocument(ph, documents);
    const { reqSig, sig } = resolveSigState(ph, doc);
    const signerRoleEntry = resolvePlaceholderRoleNameEntries(
      ph?.SignerRoles ?? ph?.signerRoles,
      roleOptions,
    )[0];
    const signerRole = signerRoleEntry?.roleName || "";
    const bucket = dueBucket(row?.DueDate ?? row?.dueDate, status);
    const siteName =
      row?.SiteName ?? row?.siteName ?? (sid === "__study__" ? "Study level" : "Site");
    const siteId = sid === "__study__" ? "__study__" : sid;

    if (!siteMap.has(siteId)) {
      siteMap.set(siteId, {
        id: siteId,
        name: siteName,
        city: "",
      });
    }

    if (!msMap.has(code)) {
      msMap.set(code, {
        code,
        name:
          row?.MilestoneName ??
          row?.milestoneName ??
          row?.PlaceholderName ??
          code,
        reqSig,
        role: ph?.SignerRoles ? String(ph.SignerRoles).split(",")[0] : "",
      });
    }

    const cellKey = `${code}|${siteId}`;
    const sigReq = reqSig ? 1 : 0;
    const sigSigned = sig === "signed" ? 1 : 0;
    const next = {
      code,
      siteId,
      siteName,
      status: isRequired ? status : "na",
      bucket,
      reqSig,
      sig,
      sigReq,
      sigSigned,
      isRequired,
      days: daysFromDue(row?.DueDate ?? row?.dueDate, status),
      due: row?.DueDate ?? row?.dueDate ?? null,
      owner: row?.AssignedToName ?? row?.assignedToName ?? "—",
      signer: row?.AssignedToName ?? "—",
      placeholderName: row?.PlaceholderName ?? row?.placeholderName ?? "",
      studyPlaceholderId: row?.StudyPlaceholderId ?? row?.studyPlaceholderId,
      documentName: row?.FulfilledDocumentName ?? row?.fulfilledDocumentName ?? "",
      signerRole,
    };

    if (cellMap.has(cellKey)) {
      const prev = cellMap.get(cellKey);
      const mergedReq = (prev.sigReq || 0) + sigReq;
      const mergedSigned = (prev.sigSigned || 0) + sigSigned;
      cellMap.set(cellKey, {
        ...next,
        status: pickWorseStatus(prev.status, next.status),
        bucket: pickWorseStatus(prev.status, next.status) === "overdue" ? "overdue" : next.bucket,
        reqSig: prev.reqSig || next.reqSig,
        sigReq: mergedReq,
        sigSigned: mergedSigned,
        sig:
          mergedReq > 0 && mergedSigned >= mergedReq
            ? "signed"
            : prev.sig === "await" || next.sig === "await"
              ? "await"
              : prev.sig === "signed" || next.sig === "signed"
                ? "signed"
                : next.sig,
      });
    } else {
      cellMap.set(cellKey, next);
    }
  });

  (siteComparison || []).forEach((row) => {
    const id = String(row?.SiteIdRef ?? row?.siteIdRef ?? "").toLowerCase();
    if (!id) return;
    siteMap.set(id, {
      id,
      name: row?.SiteName ?? row?.siteName ?? "Site",
      city: "",
      readinessPct: Number(row?.ReadinessPercent ?? row?.readinessPercent ?? 0),
    });
  });

  (milestoneSummary || []).forEach((row) => {
    const code = milestoneCode(row);
    if (!msMap.has(code)) {
      msMap.set(code, {
        code,
        name: row?.MilestoneName ?? row?.milestoneName ?? code,
        reqSig: false,
        role: "",
      });
    }
  });

  const sites = [...siteMap.values()].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );
  const milestones = [...msMap.values()].sort((a, b) =>
    a.code.localeCompare(b.code, undefined, { sensitivity: "base" }),
  );
  const cells = [...cellMap.values()];

  const studySummary = {
    readinessPct: Number(summary?.ReadinessPercent ?? summary?.readinessPercent ?? 0),
    requiredTotal: Number(summary?.RequiredTotal ?? 0),
    requiredFulfilled: Number(summary?.RequiredFulfilled ?? 0),
    overdueCount: Number(summary?.OverdueCount ?? 0),
    dueWithin7: Number(summary?.DueWithin7Days ?? 0),
    dueWithin14: Number(summary?.DueWithin14Days ?? 0),
    dueWithin30: Number(summary?.DueWithin30Days ?? 0),
  };

  return {
    sites,
    milestones,
    cells,
    atRiskDetail: atRiskDetail || [],
    studySummary,
  };
}

export function formatGbDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
